// Archie Chat Input Component
// A reusable gradient-bordered input component for Archie showcase cards

import { motion, Variants } from 'framer-motion';

// ==========================================
// ANIMATION VARIANTS
// ==========================================
const itemVariants: Variants = {
  hidden: {
    opacity: 0,
    y: 16,
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.4,
      ease: [0.25, 0.46, 0.45, 0.94],
    },
  },
};

// ==========================================
// SEND ICON COMPONENT
// ==========================================
const SendIcon = ({ className, style }: { className?: string; style?: React.CSSProperties }) => (
  <svg className={className} style={style} viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M14 2L7 9M14 2L9.5 14L7 9M14 2L2 6.5L7 9"
      stroke="currentColor"
      strokeWidth="1.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// ==========================================
// SIZE CONFIGURATIONS
// =========================================
type SizeConfig = {
  padding: number;
  gap: number;
  borderRadius: number;
  fontSize: number;
  lineHeight: number;
  sendIconSize: number;
  sendButtonPadding: number;
};

const sizeConfigs: Record<string, SizeConfig> = {
  lg: {
    padding: 10.6,
    gap: 13.3,
    borderRadius: 9,
    fontSize: 10.6,
    lineHeight: 15.9,
    sendIconSize: 10.6,
    sendButtonPadding: 5.3,
  },
  md: {
    padding: 9,
    gap: 12,
    borderRadius: 9,
    fontSize: 9.4,
    lineHeight: 14,
    sendIconSize: 9,
    sendButtonPadding: 5,
  },
  sm: {
    padding: 6,
    gap: 8,
    borderRadius: 6,
    fontSize: 7,
    lineHeight: 10,
    sendIconSize: 7,
    sendButtonPadding: 3,
  },
};

// ==========================================
// COMPONENT PROPS
// ==========================================
interface ArchieChatInputProps {
  size: 'sm' | 'md' | 'lg';
  placeholder: string;
  className?: string;
}

// ==========================================
// ARCHIE CHAT INPUT COMPONENT
// ==========================================
export const ArchieChatInput = ({ size, placeholder, className = '' }: ArchieChatInputProps) => {
  const config = sizeConfigs[size];

  return (
    <motion.div
      className={`relative ${className}`}
      variants={itemVariants}
    >
      {/* Gradient border using ::before pseudo-element is handled via inline styles below */}
      <div
        className="relative"
        style={{
          // Create space for the gradient border
          padding: 1,
          borderRadius: config.borderRadius,
          background: 'linear-gradient(180deg, rgba(185, 188, 203, 1) 0%, rgba(229, 231, 240, 1) 100%)',
        }}
      >
        {/* Inner content with white background */}
        <div
          className="bg-white flex flex-col w-full"
          style={{
            paddingLeft: config.padding,
            paddingRight: config.padding,
            paddingTop: config.padding,
            paddingBottom: config.padding,
            gap: config.gap,
            borderRadius: config.borderRadius - 1,
            boxShadow: '0px 2px 4px rgba(0,0,0,0.08), 0px 4px 8px rgba(0,0,0,0.04)',
          }}
        >
          {/* Placeholder text */}
          <span
            className="text-gray-600"
            style={{
              fontFamily: "'Inter', sans-serif",
              fontWeight: 400,
              fontSize: config.fontSize,
              lineHeight: `${config.lineHeight}px`,
            }}
          >
            {placeholder}
          </span>
          
          {/* Send button row */}
          <div className="flex justify-end">
            <div
              className="bg-gray-200 flex items-center justify-center"
              style={{
                padding: config.sendButtonPadding,
                borderRadius: 100,
              }}
            >
              <SendIcon
                className="text-gray-600"
                style={{
                  width: config.sendIconSize,
                  height: config.sendIconSize,
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default ArchieChatInput;
